import {
	Box,
	createStyles,
	Group,
	Modal,
	Skeleton,
	Stack,
} from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import {
	useApiAddBiDashboardMetrics,
	useApiCreateDraftBiMetric,
	useApiUpdateBiDashboard,
	useSuspenseApiGetBiDashboard,
} from '@repo/api-codegen';
import { Button, DnD, Icon } from '@repo/foundations';

import { useQueryClient } from '@tanstack/react-query';
import { observer } from 'mobx-react-lite';
import {
	memo,
	Suspense,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from 'react';
import { Helmet } from 'react-helmet';
import type { ISecodaEntity } from '../../api';
import { useAuthUser, useUpdateSecodaEntity, useWorkspace } from '../../api';
import {
	PageLayoutContent,
	PageLayoutContentWrapper,
	PageLayoutOuterWrapper,
	PageLayoutWrapper,
} from '../../components/PageLayout';
import { RightSidebarWrapperController } from '../../components/RightSidebar/RightSidebarWrapper';

import { useDisclosure } from '@mantine/hooks';
import { EntityType } from '@repo/common/enums/entityType';
import { UserRole } from '@repo/common/enums/UserRole';
import type { DnDLayoutItem } from '@repo/foundations/components/DnD/types';
import { isEmpty, isNil } from 'lodash-es';
import { useParams } from 'react-router';
import EntityDiscussions from '../../components/EntityDiscussions/EntityDiscussions.tsx';
import { getEntityPageSidebarProps } from '../../components/EntityDrawer/utils';
import EntityPageSidebar from '../../components/EntityPageLayout/EntityPageSidebar';
import { useLineageTab } from '../../components/EntityPageLayout/hooks.ts';
import { LineageTab } from '../../components/LineageGraph/LineageTab.tsx';
import OverviewPageTabs from '../../components/Overview/OverviewPageTabs.tsx';
import { RichEditor } from '../../components/RichEditor/index.tsx';
import {
	RightSidebarStore,
	rightSidebarStore,
} from '../../components/RightSidebar/store.ts';
import { useDebouncedCallbackWithFlush } from '../../hooks/useDebouncedCallbackWithFlush';
import type { DjangoValueType } from '../../pages/TemplatePage/types';
import { useCheckIAMPermission } from '../../utils/authorization/roles';
import { useTabNavigate } from '../../utils/hook/useTabNavigate.ts';
import { AiSideChatForObjectController } from '../AiSideChatForObject/AiSideChatForObjectController.tsx';
import type { BIMetricStore } from '../BiMetric';
import { BiMetricController } from '../BiMetric';
import { BiMetricPageController } from '../BiMetric/BiMetricPageController.tsx';
import { BiDashboardNavBarController } from './BiDashboardNavBarController.tsx';
import { BiDashboardEmptyState } from './components/BiDashboardEmptyState.tsx';
import { BIDashboardPageHeader } from './components/BiDashboardHeader.tsx';
import { BiDashboardSkeleton } from './components/BiDashboardSkeleton.tsx';
import { BIDashboardStore } from './store.ts';
import type { BIDashboardMode } from './types';
import { generateMetricsLayout } from './utils/generateMetricLayout';
import { trackDashboardViewed, trackMetricCreated } from './utils/tracking';

interface BIDashboardPageControllerProps {
	id: string;
	mode: BIDashboardMode;
	onCloseCanvas?: () => void;
	onMetricHeaderClick?: (metricId: string) => void;
}

const useStyles = createStyles((theme) => ({
	contentWrapper: {
		flexGrow: 1,
		gap: theme.spacing.xl,
	},
	modalBody: {
		padding: '0 !important',
		backgroundColor: `${theme.other.getColor('surface/app/default')} !important`,
		height: '70vh',
	},
	dashboardContainer: {
		// Remove border radius and shadow from nested Card components only
		'& .mantine-Card-root .mantine-Card-root': {
			borderRadius: '0',
			boxShadow: 'none',
		},
		// Set table cell text size to sm when inside a Card
		'& .mantine-Card-root .mantine-DataTable-root': {
			'& td, & th': {
				fontSize: theme.fontSizes.sm,
			},
		},
		margin: `0 -${theme.spacing.xs}`,
	},
}));

const DEBOUNCED_NOTIFICATION_WAIT = 1500;

const BIDashboardPageControllerInternal = observer(
	({
		id,
		mode,
		onCloseCanvas,
		onMetricHeaderClick,
	}: BIDashboardPageControllerProps) => {
		const queryClient = useQueryClient();
		const navigateToTab = useTabNavigate();

		const { user } = useAuthUser();
		const { workspace } = useWorkspace();
		const { tab: urlTabParam } = useParams();
		const { theme, classes } = useStyles();

		const [
			newMetricModalOpened,
			{ open: openNewMetricModal, close: closeNewMetricModal },
		] = useDisclosure(false);

		const [draftNewMetricId, setDraftNewMetricId] = useState<string | null>(
			null
		);

		const { data: dashboard, queryKey: dashboardQueryKey } =
			useSuspenseApiGetBiDashboard({
				pathParams: {
					biDashboardId: id,
				},
			});
		const dashboardStore = useMemo(
			() => new BIDashboardStore({ dashboard, user, workspace }),
			[dashboard, user, workspace]
		);

		const { hasPermission: hasManagePermission } = useCheckIAMPermission({
			v1AllowedRoles: [UserRole.ADMIN, UserRole.EDITOR],
			v2Permission: 'Dashboards.Manage',
		});

		useEffect(() => {
			trackDashboardViewed(id, user, workspace);
		}, [id, user, workspace]);

		// Cleanup on unmount
		// eslint-disable-next-line no-use-effect/no-use-effect
		useEffect(() => () => dashboardStore.dispose(), [dashboardStore]);

		const {
			mutateAsync: addMetricsToDashboard,
			isPending: isAddingMetricsToDashboard,
		} = useApiAddBiDashboardMetrics({});
		const { mutateAsync: createDraftNewMetric } = useApiCreateDraftBiMetric({});
		const { mutateAsync: updateDashboard } = useApiUpdateBiDashboard({});

		const showDebouncedNotification = useDebouncedCallbackWithFlush(() => {
			showNotification({
				title: 'Dashboard updated',
				message: 'The dashboard has been updated successfully',
				color: 'green',
				icon: <Icon name="check" color="text/success-on-fill/default" />,
			});
		}, DEBOUNCED_NOTIFICATION_WAIT);

		const { mutateAsync: updateDashboardSecodaEntity } = useUpdateSecodaEntity({
			options: {
				onSuccess: () => {
					queryClient.invalidateQueries({
						queryKey: dashboardQueryKey,
					});

					showDebouncedNotification();
				},
			},
		});

		const dashboardRightSidebarStore = useMemo(() => {
			if (mode === 'page') {
				return rightSidebarStore;
			}

			return new RightSidebarStore();
		}, [mode]);

		const commitDraftMetricToDashboard = async (store: BIMetricStore) => {
			await store.saveChanges({ isDraft: false });
			await addMetricsToDashboard({
				pathParams: {
					biDashboardId: id,
				},
				body: { metric_ids: [store.metricId] },
			});

			trackMetricCreated('manual', store.metricId, user, workspace);

			queryClient.invalidateQueries({
				queryKey: dashboardQueryKey,
			});

			closeNewMetricModal();
			setDraftNewMetricId(null);
		};

		const onClickAddMetric = useCallback(async () => {
			let newMetricId = draftNewMetricId;
			if (!newMetricId) {
				const newMetric = await createDraftNewMetric({});
				setDraftNewMetricId(newMetric.id);
				newMetricId = newMetric.id;
			}

			openNewMetricModal();
		}, [createDraftNewMetric, draftNewMetricId, openNewMetricModal]);

		const handleTabChange = useCallback(
			(tab: string) => {
				navigateToTab(tab);
			},
			[navigateToTab]
		);

		const handleUpdateDashboardLayout = useCallback(
			async (layout: Record<string, DnDLayoutItem>) => {
				await updateDashboard({
					pathParams: {
						biDashboardId: id,
					},
					body: { layout },
				});
				showDebouncedNotification();
			},
			[id, showDebouncedNotification, updateDashboard]
		);

		const handleUpdateDashboardSecodaEntity = useCallback(
			(key: string, value: DjangoValueType) => {
				updateDashboardSecodaEntity({
					data: {
						id: dashboard.secoda_entity.id,
						[key]: value,
					},
				});
			},
			[dashboard.secoda_entity.id, updateDashboardSecodaEntity]
		);

		const handleUpdateDashboardDocumentation = useCallback(
			(value: DjangoValueType) => {
				if (dashboard.secoda_entity.definition === value) {
					return;
				}
				handleUpdateDashboardSecodaEntity('definition', value);
			},
			[dashboard.secoda_entity.definition, handleUpdateDashboardSecodaEntity]
		);

		const metrics = useMemo(
			() =>
				dashboard.bi_metrics.map((metricId: string) => ({
					id: metricId,
				})),
			[dashboard.bi_metrics]
		);

		// This make sure the key changes when there's deletion/addition of metrics, and we re-render the DnD component
		const dashboardMetricsKey = useMemo(
			() =>
				`dashboard-${id}-metrics-${metrics
					.toSorted((a, b) => a.id.localeCompare(b.id))
					.map((m) => m.id)
					.join('-')}`,
			[id, metrics]
		);

		const { lineageFlowStore, lineageFlowUIStore, lineageFilterStore } =
			useLineageTab(
				dashboard.secoda_entity.id,
				dashboard.secoda_entity.published,
				EntityType.bi_dashboard,
				dashboard.secoda_entity.native_type
			);

		const tabs = useMemo(
			() => [
				{
					value: 'dashboard',
					label: 'Dashboard',
					component: isEmpty(metrics) ? (
						<BiDashboardEmptyState
							onAddMetric={onClickAddMetric}
							hasUseButtonPermission={hasManagePermission}
						/>
					) : (
						<Box className={classes.dashboardContainer}>
							<DnD
								key={dashboardMetricsKey}
								items={metrics}
								layout={
									isNil(dashboard.layout) ||
									Object.keys(dashboard.layout as Record<string, DnDLayoutItem>)
										.length === 0
										? generateMetricsLayout(metrics)
										: (dashboard.layout as Record<string, DnDLayoutItem>)
								}
								onLayoutChange={
									hasManagePermission ? handleUpdateDashboardLayout : undefined
								}
								renderItem={({ activatorRef, item, listeners, isDragging }) => (
									<BiMetricController
										metricId={item.id}
										mode="view_only"
										dashboardStore={dashboardStore}
										onHeaderClick={() => {
											onMetricHeaderClick?.(item.id);
										}}
										activatorRef={activatorRef}
										listeners={hasManagePermission ? listeners : undefined}
										isDragging={isDragging}
									/>
								)}
							/>
						</Box>
					),
				},
				{
					value: 'documentation',
					label: 'Documentation',
					component: (
						<RichEditor
							id={dashboard.secoda_entity.id}
							onChangeCallback={handleUpdateDashboardDocumentation}
							initialValue={dashboard.secoda_entity.definition ?? ''}
							readOnly={!hasManagePermission}
							disableExtensions={[]}
							onChangeCallbackDebounceWait={500}
							dataTestId="bi-dashboard-documentation"
							enableScrollToHash={false}
						/>
					),
				},
				{
					value: 'lineage',
					label: 'Lineage',
					component: (
						<LineageTab
							lineageFlowStore={lineageFlowStore}
							lineageFlowUIStore={lineageFlowUIStore}
							lineageFilterStore={lineageFilterStore}
						/>
					),
				},
				{
					value: 'discussions',
					label: 'Questions',
					component: <EntityDiscussions entity={dashboard.secoda_entity} />,
				},
			],
			[
				metrics,
				onClickAddMetric,
				classes.dashboardContainer,
				dashboardMetricsKey,
				dashboard.layout,
				dashboard.secoda_entity,
				handleUpdateDashboardLayout,
				handleUpdateDashboardDocumentation,
				lineageFlowStore,
				lineageFlowUIStore,
				lineageFilterStore,
				dashboardStore,
				onMetricHeaderClick,
				hasManagePermission,
			]
		);

		const currentTab = urlTabParam ?? 'dashboard';
		const startingPointEntityIdentifier = useMemo(
			() => `bi_dashboard:${id}`,
			[id]
		);

		return (
			<PageLayoutOuterWrapper>
				<Helmet>
					<title>{dashboard?.secoda_entity.title || 'BI Dashboard'}</title>
				</Helmet>
				<PageLayoutWrapper name="bi-dashboard-page" key={id}>
					<Modal
						opened={newMetricModalOpened}
						onClose={closeNewMetricModal}
						title="Add metric"
						withinPortal
						keepMounted
						size={theme.other.width.xxl}
						classNames={{
							body: classes.modalBody,
						}}
					>
						{!draftNewMetricId && (
							<Stack spacing="md" h="100%" p="md">
								<Skeleton height="100%" width="100%" radius="md" />
							</Stack>
						)}
						{draftNewMetricId && (
							<BiMetricPageController
								initialTab="chart"
								isCreation={true}
								modalFooter={(store) => (
									<Group
										position="right"
										p="md"
										sx={{
											flex: 0,
										}}
									>
										<Button variant="default" onClick={closeNewMetricModal}>
											Cancel
										</Button>
										<Button
											variant="primary"
											loading={
												isAddingMetricsToDashboard || store.isSavingChanges
											}
											onClick={() => commitDraftMetricToDashboard(store)}
										>
											Add metric
										</Button>
									</Group>
								)}
								metricId={draftNewMetricId}
								mode="modal"
								startingPointEntityIdentifier={`new_bi_metric_for_bi_dashboard:${id}:draft_bi_metric_id:${draftNewMetricId}`}
							/>
						)}
					</Modal>
					<PageLayoutContentWrapper name="bi-dashboard-page">
						<BiDashboardNavBarController
							id={id}
							entity={dashboard.secoda_entity}
							mode={mode}
							user={user}
							dashboardRightSidebarStore={dashboardRightSidebarStore}
							dashboardStore={dashboardStore}
							onCloseCanvas={onCloseCanvas}
							onAddMetric={onClickAddMetric}
						/>
						<PageLayoutContent>
							<Stack
								className={classes.contentWrapper}
								pt="sm"
								data-bi-dashboard-export-target={id}
							>
								<BIDashboardPageHeader
									entity={dashboard.secoda_entity}
									mode={mode}
									isReadOnly={!hasManagePermission}
									onIconChange={(value) =>
										handleUpdateDashboardSecodaEntity('icon', value)
									}
									onTitleChange={(value) =>
										handleUpdateDashboardSecodaEntity('title', value)
									}
									onDescriptionChange={(value) =>
										handleUpdateDashboardSecodaEntity('description', value)
									}
								/>

								{mode === 'page' ? (
									<OverviewPageTabs
										key={dashboard.secoda_entity.id}
										defaultTab={'dashboard'}
										currentTab={currentTab}
										tabs={tabs}
										onTabChange={handleTabChange}
									/>
								) : (
									<Box className={classes.dashboardContainer}>
										<DnD
											key={dashboardMetricsKey}
											items={metrics}
											layout={
												isNil(dashboard.layout) ||
												Object.keys(
													dashboard.layout as Record<string, DnDLayoutItem>
												).length === 0
													? generateMetricsLayout(metrics)
													: (dashboard.layout as Record<string, DnDLayoutItem>)
											}
											onLayoutChange={
												hasManagePermission
													? handleUpdateDashboardLayout
													: undefined
											}
											renderItem={({
												activatorRef,
												item,
												listeners,
												isDragging,
											}) => (
												<BiMetricController
													key={`metric-${item.id}`}
													metricId={item.id}
													mode="view_only"
													dashboardStore={dashboardStore}
													onHeaderClick={() => {
														onMetricHeaderClick?.(item.id);
													}}
													activatorRef={activatorRef}
													listeners={
														hasManagePermission ? listeners : undefined
													}
													isDragging={isDragging}
												/>
											)}
										/>
									</Box>
								)}
							</Stack>
						</PageLayoutContent>
					</PageLayoutContentWrapper>
					<RightSidebarWrapperController store={dashboardRightSidebarStore}>
						{dashboardRightSidebarStore.mode === 'info' && (
							<EntityPageSidebar
								entity={dashboard.secoda_entity as unknown as ISecodaEntity} // TODO: Don't use `as unknown as`
								updateEntity={(key, value) =>
									handleUpdateDashboardSecodaEntity(key, value)
								}
								{...getEntityPageSidebarProps(EntityType.bi_dashboard)}
							/>
						)}
						{dashboardRightSidebarStore.mode === 'ai' && (
							<AiSideChatForObjectController
								startingPointEntityIdentifier={startingPointEntityIdentifier}
							/>
						)}
					</RightSidebarWrapperController>
				</PageLayoutWrapper>
			</PageLayoutOuterWrapper>
		);
	}
);

export const BiDashboardPageController = memo(
	(props: BIDashboardPageControllerProps) => (
		<Suspense fallback={<BiDashboardSkeleton />}>
			<BIDashboardPageControllerInternal {...props} />
		</Suspense>
	)
);
